/** BTH CRM — display and store dates in Singapore time (UTC+8). */
export const CRM_TIMEZONE = 'Asia/Singapore';

/** User-facing dates use British locale for DD/MM/YYYY ordering. */
export const CRM_DISPLAY_LOCALE = 'en-GB';

export function crmDateFormatOptions(): Intl.DateTimeFormatOptions {
  return {
    timeZone: CRM_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  };
}

export function crmDateTimeFormatOptions(): Intl.DateTimeFormatOptions {
  return {
    timeZone: CRM_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  };
}

export function crmDateTimeWithSecondsFormatOptions(): Intl.DateTimeFormatOptions {
  return {
    ...crmDateTimeFormatOptions(),
    second: '2-digit',
  };
}

/** Task due date text field — DD/MM/YYYY with 12-hour clock. */
export function crmDueDateTime12hFormatOptions(): Intl.DateTimeFormatOptions {
  return {
    timeZone: CRM_TIMEZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: true,
  };
}

export function crmMonthYearFormatOptions(): Intl.DateTimeFormatOptions {
  return {
    timeZone: CRM_TIMEZONE,
    month: '2-digit',
    year: 'numeric',
  };
}

export function parseCrmDate(iso?: string | null): Date | null {
  if (iso == null || iso === '') return null;
  let raw = String(iso).trim();
  if (!raw) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    const d = new Date(`${raw}T12:00:00+08:00`);
    return Number.isNaN(d.getTime()) ? null : d;
  }
  // Postgres / Cartrack style: "2026-09-14 16:41:00+08"
  raw = raw.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
  raw = raw.replace(/([+-]\d{2})$/, '$1:00');
  raw = raw.replace(/\s+([+-]\d{2}:\d{2})$/, '$1');
  const d = new Date(raw);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** YYYY-MM-DD → DD/MM/YYYY (no timezone shift). */
export function formatYmdAsCrmDate(ymd: string): string {
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(ymd.trim());
  if (!m) return ymd.trim() || '—';
  return `${m[3]}/${m[2]}/${m[1]}`;
}

/** YYYY-MM → MM/YYYY */
export function formatYmAsCrmMonthYear(ym: string): string {
  const m = /^(\d{4})-(\d{2})$/.exec(ym.trim());
  if (!m) return ym.trim();
  return `${m[2]}/${m[1]}`;
}

/** Month picker label from YYYY-MM. */
export function formatCrmMonthYear(monthKey: string): string {
  return formatYmAsCrmMonthYear(monthKey);
}

/** e.g. 28/05/2026 */
export function formatSingaporeDate(iso?: string | null): string {
  const raw = iso?.trim();
  if (!raw) return '—';
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return formatYmdAsCrmDate(raw);
  const d = parseCrmDate(raw);
  if (!d) return '—';
  return new Intl.DateTimeFormat(CRM_DISPLAY_LOCALE, crmDateFormatOptions()).format(d);
}

/** Parses unix seconds stored in system_config version stamps (e.g. "1783346268.09"). */
export function parseMasterDataVersionUnix(raw?: string | null): Date | null {
  if (!raw?.trim()) return null;
  const seconds = Number.parseFloat(raw.trim());
  if (!Number.isFinite(seconds) || seconds <= 0) return null;
  const d = new Date(seconds * 1000);
  return Number.isNaN(d.getTime()) ? null : d;
}

/** e.g. 06/07/2026, 21:57 — from master data sync version stamp. */
export function formatMasterDataVersion(raw?: string | null): string {
  const d = parseMasterDataVersionUnix(raw);
  if (!d) return '—';
  return new Intl.DateTimeFormat(CRM_DISPLAY_LOCALE, crmDateTimeFormatOptions()).format(d);
}

/** Short relative label for quick daily checks (Today / Yesterday / date). */
export function formatMasterDataVersionRelative(raw?: string | null): string | null {
  const d = parseMasterDataVersionUnix(raw);
  if (!d) return null;

  const dayFmt = new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  });
  const versionDay = dayFmt.format(d);
  const today = dayFmt.format(new Date());
  if (versionDay === today) return 'Today';

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  if (versionDay === dayFmt.format(yesterday)) return 'Yesterday';

  return null;
}

/** e.g. "Just now", "3 min ago" — for live presence lists. */
export function formatLastSeenRelative(iso?: string | null): string {
  const d = parseCrmDate(iso);
  if (!d) return '—';

  const seconds = Math.max(0, Math.floor((Date.now() - d.getTime()) / 1000));
  if (seconds < 45) return 'Just now';

  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;

  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;

  return formatSingaporeDateTime(iso);
}

/** e.g. 28/05/2026, 3:30 pm */
export function formatSingaporeDateTime12h(iso?: string | null): string {
  const d = parseCrmDate(iso);
  if (!d) return '—';
  return new Intl.DateTimeFormat(CRM_DISPLAY_LOCALE, crmDueDateTime12hFormatOptions()).format(d);
}

/** e.g. 28/05/2026, 15:30 */
export function formatSingaporeDateTime(iso?: string | null): string {
  const d = parseCrmDate(iso);
  if (!d) return '—';
  return new Intl.DateTimeFormat(CRM_DISPLAY_LOCALE, crmDateTimeFormatOptions()).format(d);
}

/** e.g. 28/05/2026, 15:30:45 */
export function formatSingaporeDateTimeWithSeconds(iso?: string | null): string {
  const raw = iso?.trim();
  if (!raw) return '—';
  const d = parseCrmDate(raw);
  if (!d) return raw;
  return new Intl.DateTimeFormat(CRM_DISPLAY_LOCALE, crmDateTimeWithSecondsFormatOptions()).format(d);
}

/** e.g. 15:30 — empty string when the value is date-only (no clock time). */
export function formatSingaporeTime(iso?: string | null): string {
  if (!iso?.trim()) return '';
  const raw = iso.trim();
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return '';
  const d = parseCrmDate(raw);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-SG', {
    timeZone: CRM_TIMEZONE,
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(d);
}

/** YYYY-MM-DD in Singapore — for DB date columns when marking completed. */
export function singaporeTodayDateString(): string {
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());
}

/** YYYY-MM-DD in Singapore for an ISO timestamp. */
export function singaporeDateStringFromIso(value: string): string {
  const d = parseCrmDate(value);
  if (!d) return '';
  return new Intl.DateTimeFormat('en-CA', {
    timeZone: CRM_TIMEZONE,
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(d);
}

/** Display text for a YYYY-MM-DD date field (DD/MM/YYYY). */
export function formatDateOnlyInputText(ymd: string): string {
  const trimmed = ymd.trim();
  if (!trimmed) return '';
  const formatted = formatYmdAsCrmDate(trimmed);
  return formatted === '—' ? '' : formatted;
}

function pad2(n: number): string {
  return String(n).padStart(2, '0');
}

function ymdFromParts(year: number, month: number, day: number): string | null {
  if (!Number.isFinite(year) || !Number.isFinite(month) || !Number.isFinite(day)) return null;
  if (year < 1000 || year > 9999 || month < 1 || month > 12 || day < 1 || day > 31) return null;
  const probe = new Date(Date.UTC(year, month - 1, day));
  if (
    probe.getUTCFullYear() !== year ||
    probe.getUTCMonth() !== month - 1 ||
    probe.getUTCDate() !== day
  ) {
    return null;
  }
  return `${year}-${pad2(month)}-${pad2(day)}`;
}

/** Parse typed date text into YYYY-MM-DD (supports DD/MM/YYYY and YYYY-MM-DD). */
export function parseDateOnlyInput(text: string): string | null {
  const trimmed = text.trim();
  if (!trimmed) return null;

  const iso = trimmed.match(/^(\d{4})[-/](\d{1,2})[-/](\d{1,2})$/);
  if (iso) return ymdFromParts(Number(iso[1]), Number(iso[2]), Number(iso[3]));

  const dmy = trimmed.match(/^(\d{1,2})[-/.](\d{1,2})[-/.](\d{4})$/);
  if (dmy) return ymdFromParts(Number(dmy[3]), Number(dmy[2]), Number(dmy[1]));

  return null;
}
